import { useEffect, useState } from 'react';
import { CornerUpLeft, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Check, Plus, Loader2, AlertTriangle, MessageSquareText } from 'lucide-react';
import api from '../../services/api';

/** Libellés courts des domaines pour les puces (les libellés longs restent dans la bibliothèque de la gestion). */
const SHORT = {
    dgfip: 'DGFiP', istqb: 'ISTQB', atsem: 'ATSEM', crpe: 'CRPE',
    territorial: 'Collectivités', logique: 'Psychotechniques', culture: 'Culture générale',
};

/**
 * « Des idées pour commencer » (étape Thème de la carte d'essai) : une idée de départ par domaine
 * (celui de la personne présélectionné), « Reprendre cette idée » (onUseIdea) et « Voir le résultat »
 * qui déplie une vraie question issue de cette idée, relue, avec l'ajout du sujet à la thématique (onAdded).
 */
export default function TrialIdeas({ disabled = false, onUseIdea, onAdded }) {
    const [examples, setExamples] = useState([]);
    const [domains, setDomains] = useState([]);
    const [domain, setDomain] = useState(null);
    const [open, setOpen] = useState(false);
    const [qi, setQi] = useState(0);
    const [showExpl, setShowExpl] = useState(false);
    const [adding, setAdding] = useState(false);
    const [error, setError] = useState('');

    const load = () => api.get('/custom/trial-examples.php')
        .then(({ data }) => {
            const d = data.data || {};
            setExamples(d.examples || []);
            setDomains(d.domains || []);
            setDomain(cur => (cur && (d.domains || []).some(x => x.key === cur)) ? cur : (d.default_domain || null));
        })
        .catch(() => {});
    useEffect(() => { load(); }, []);

    const ex = examples.find(x => x.domain === domain) || examples[0] || null;
    useEffect(() => { setQi(0); setShowExpl(false); }, [ex?.key]);
    if (!ex) return null;
    const q = ex.preview[qi] || ex.preview[0];

    const add = async () => {
        setAdding(true);
        setError('');
        try {
            const { data } = await api.post('/custom/trial-examples.php', { key: ex.key });
            load();
            onAdded?.({ thematiqueId: data.data.thematique_id, slug: data.data.slug, sousSujetId: data.data.sous_sujet_id });
        } catch (err) {
            setError(err.response?.data?.message || 'L’exemple n’a pas pu être ajouté. Réessayez dans un instant.');
        } finally {
            setAdding(false);
        }
    };

    return (
        <section className="trial3-ideas">
            <h4 className="trial3-ideas__title">Des idées pour commencer</h4>
            <p className="trial3-ideas__sub">Découvrez une idée, son résultat, puis adaptez-la à vos révisions.</p>
            {domains.length > 1 && (
                // Vignettes au visuel des thématiques (image + couleur), même esprit que le bandeau de l'en-tête.
                <div className="trial3-scopes" role="tablist" aria-label="Domaine">
                    {domains.map(d => (
                        <button key={d.key} type="button" role="tab" aria-selected={domain === d.key}
                            className={`trial3-scope${d.image ? ' has-image' : ''}${domain === d.key ? ' is-active' : ''}`}
                            style={{
                                ...(d.color ? { '--scope-color': d.color } : {}),
                                ...(d.image ? { backgroundImage: `url(${d.image})` } : {}),
                            }}
                            onClick={() => { setDomain(d.key); setOpen(false); }} disabled={disabled}>
                            <span>{SHORT[d.key] || d.label}</span>
                        </button>
                    ))}
                </div>
            )}

            <div className="trial3-idea">
                <span className="trial3-idea__label"><MessageSquareText size={14} /> L’idée de départ</span>
                <p>{ex.consignes}</p>
                <div className="trial3-idea__actions">
                    <button type="button" className="trial3-btn-light" onClick={() => onUseIdea?.({ label: ex.label, consignes: ex.consignes })} disabled={disabled}>
                        <CornerUpLeft size={14} /> Reprendre cette idée
                    </button>
                    <button type="button" className="trial3-toggle" onClick={() => setOpen(o => !o)}>
                        {open ? 'Masquer le résultat' : 'Voir le résultat'} {open ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                    </button>
                </div>
            </div>

            {open && (
                <div className="trial3-result">
                    <div className="trial3-result__bar">
                        <span>{ex.label}</span>
                        <span className="trial3-result__nav">
                            <button type="button" onClick={() => setQi(i => Math.max(0, i - 1))} disabled={qi === 0} aria-label="Question précédente"><ChevronLeft size={14} /></button>
                            Extrait {qi + 1} / {ex.preview.length}
                            <button type="button" onClick={() => setQi(i => Math.min(ex.preview.length - 1, i + 1))} disabled={qi >= ex.preview.length - 1} aria-label="Question suivante"><ChevronRight size={14} /></button>
                        </span>
                    </div>
                    <p className="trial3-result__q">{q.enonce}</p>
                    <ol className="trial3-answers">
                        {Object.entries(q.reponses).map(([k, v], i) => (
                            <li key={k} className={showExpl && k === q.bonne_reponse ? 'is-good' : ''}>
                                <span className="trial3-answers__letter">{showExpl && k === q.bonne_reponse ? <Check size={13} /> : 'ABCD'[i]}</span>{v}
                            </li>
                        ))}
                    </ol>
                    <button type="button" className="trial3-toggle" onClick={() => setShowExpl(v => !v)}>
                        {showExpl ? 'Masquer la correction' : 'Voir la correction'}
                    </button>
                    {showExpl && (
                        <div className="trial3-expl">
                            {String(q.explication).split(/\n\s*\n/).map((para, j) => (
                                // Contenu relu, stocké côté serveur : seules les balises <strong> sont conservées.
                                <p key={j} dangerouslySetInnerHTML={{ __html: para.replace(/<(?!\/?strong>)[^>]*>/g, '') }} />
                            ))}
                        </div>
                    )}
                    {error && <p className="trial-card__error"><AlertTriangle size={14} /> {error}</p>}
                    <div className="trial3-result__foot">
                        {ex.added ? (
                            <span className="trial-lib__added"><Check size={14} /> Ce sujet est dans votre thématique</span>
                        ) : onAdded && (
                            <>
                                <button type="button" className="trial3-btn-light" onClick={add} disabled={disabled || adding}>
                                    {adding ? <Loader2 size={14} className="spin" /> : <Plus size={14} />} Ajouter ces {ex.nb_questions} questions à ma thématique
                                </button>
                                <span className="trial3-hint">Immédiat, et sans utiliser votre création gratuite.</span>
                            </>
                        )}
                    </div>
                </div>
            )}
        </section>
    );
}
